import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowRight } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/content";
import { CTAButton } from "@/components/ui-bits";
import { lineParent, lineChild } from "@/components/motion-primitives";

const MaskLine = ({ children, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span variants={lineChild} className={`block ${className}`}>{children}</motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden bg-brand-dark grain">
      {/* Parallax background image */}
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Johannesburg city skyline representing municipal and property services across South Africa"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/70 to-brand-dark/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/80 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 container mx-auto px-4 md:px-8 pb-16 md:pb-24 pt-32">
        <motion.div variants={lineParent} initial="hidden" animate="show" className="max-w-4xl">
          <MaskLine className="overline text-secondary mb-6">
            Municipal Expertise · Professional Management · Less Stress
          </MaskLine>

          <h1 className="text-white font-heading font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
            <MaskLine>Professional Municipal</MaskLine>
            <MaskLine>Account Management &</MaskLine>
            <MaskLine className="text-secondary font-normal">Utility Consulting</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed"
          >
            Dealing with incorrect billing, meter readings and unresolved council queries is frustrating and
            time-consuming. SML Municipal Consulting provides professional account management, billing dispute
            assistance, metering services and utility consulting for residential, commercial, industrial and
            property-management clients across South Africa.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4"
          >
            <CTAButton href="#quote" variant="secondary" size="lg" icon={ArrowRight} testId="hero-quote-btn">Request a Quote</CTAButton>
            <CTAButton href="#services" variant="outline" size="lg" testId="hero-services-btn">View Our Services</CTAButton>
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="hero-call-link"
              className="inline-flex items-center gap-2.5 text-white font-semibold group ml-0 sm:ml-2"
            >
              <span className="flex items-center justify-center w-11 h-11 rounded-full border border-white/25 group-hover:bg-secondary group-hover:border-secondary transition-colors">
                <Phone className="w-4 h-4" />
              </span>
              <span className="leading-tight">
                <span className="block text-[11px] uppercase tracking-wider text-white/50">Call us</span>
                {CONTACT.phone}
              </span>
            </a>
          </motion.div>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}
          className="w-px h-8 bg-gradient-to-b from-secondary to-transparent"
        />
      </div>
    </section>
  );
};
